import { Injectable } from '@angular/core';
import { ApiClient } from '../api.client';

import moment from 'moment';
import { map, Observable } from 'rxjs';
import { HireItem, Item } from '../../models';
import { BookingItem } from '../../models/booking-item';
import { Delivery } from '../../models/deliveries/delivery';
import { DeliveryDashboardItem } from '../../models/deliveries/delivery-dashboard-item';
import { DeliveryDisplayItem } from '../../models/deliveries/delivery-display-item';
import { BookingItemNamePipe } from '../pipes/booking-item-name.pipe';
import { DeliveryJobForUser } from './user-jobs.service';

@Injectable()
export class DeliveryService {
  constructor(
    private http: ApiClient,
    private bookingItemNamePipe: BookingItemNamePipe
  ) {}

  get(id, include = []): Observable<Delivery> {
    let qs = this.http.getWiths(include);
    return this.http.get<Delivery>(`delivery/${id}${qs}`);
  }

  getPending() {
    return this.http
      .get<{ data: DeliveryJobForUser[] }>(`delivery/pending`)
      .pipe(map((deliveries) => deliveries.data));
  }

  create(attributes) {
    return this.http.post<Delivery>('delivery', attributes);
  }

  update(id: number, docket: any) {
    return this.http.put('delivery/' + id, docket);
  }

  delete(id: number) {
    return this.http.delete(`delivery/${id}`);
  }

  complete(id, fulfilled) {
    return this.http.post(`delivery/${id}/fulfill`, fulfilled);
  }

  deliveriesToDashboardItems(
    value: DeliveryJobForUser[]
  ): DeliveryDashboardItem[] {
    return value.map((delivery) => {
      return <DeliveryDashboardItem>{
        delivery_id: delivery.id,
        customer: delivery.customer,
        customer_id: delivery.customer_id,
        deliverable_id: delivery.deliverable_id,
        deliverable_type: delivery.deliverable_type,
        itemNames: delivery.items
          .map((deliveryItem) => deliveryItem.delivery_note_description)
          .join(', '),
        status: delivery.fulfilled_date ? 'Complete' : 'Pending',
        due: new Date(delivery.due),
        location: delivery.location,
        type: delivery.type === 'delivery' ? 'Delivery' : 'Collection',
        delivery: delivery,
        created_at: new Date(delivery.created_at),
        created_by: delivery.created_by,
        allocated_to: delivery.allocated_to
      };
    });
  }

  deliveriesToCalendarItems(deliveries: DeliveryJobForUser[]) {
    return deliveries.map((delivery) => {
      let firstCategoryColor;
      // delivery items do not contain cateogry information any longer - not sure if required
      // delivery.items.forEach((item) => {
      //   if (
      //     item.deliverable_item.category &&
      //     item.deliverable_item.category.color
      //   ) {
      //     firstCategoryColor = hexToRgbLuma(
      //       item.deliverable_item.category.color
      //     );
      //   }
      // });

      if (!firstCategoryColor) {
        firstCategoryColor = {
          r: 30,
          g: 144,
          b: 255,
          luma: 20
        };
      }

      let deliveryDateMoment = moment(delivery.due);
      return {
        title: `${delivery.customer} - ${delivery.location}`,
        start: deliveryDateMoment.toDate(),
        end: deliveryDateMoment.add(1, 'hour').toDate(),
        color: {
          primary: '#237C3E',
          secondary: '#e9fff3'
        },
        draggable: false,
        cssClass: 'available',
        resizable: {
          beforeStart: false,
          afterEnd: false
        },
        item: delivery
      };
    });
  }

  mapHireItemToDeliveryItem(
    item: HireItem,
    deliverDefault = false
  ): DeliveryDisplayItem[] {
    if (item.hireable_type === 'product') {
      return item.children.map((child) => ({
        name: `${(child.hireable as Item).code} - ${(child.hireable as Item).product.name}`,
        id: child.id,
        item: child.hireable as Item,
        deliver: deliverDefault
      }));
    }
    return [
      {
        name: `${(item.hireable as Item).code} - ${(item.hireable as Item).product.name}`,
        id: item.id,
        item: item.hireable as Item,
        deliver: deliverDefault
      }
    ];
  }

  mapBookingItemToDeliveryItem(
    item: BookingItem,
    deliverDefault = false
  ): DeliveryDisplayItem {
    return {
      name: this.bookingItemNamePipe.transform(item, true),
      id: item.id,
      deliver: deliverDefault,
      item: item.item
    };
  }

  getDeliveryItems(deliveryId: number) {
    return this.http.get<DeliveryItemsResponse>(`delivery/${deliveryId}/items`);
  }

  print(id: number) {
    return this.http.getResponse(`delivery/${id}/print`);
  }
}

interface DeliveryItemsResponse {
  deliveryItems: {
    id: number;
    name: string;
    needsAssigned: boolean;
    sourceItem: BookingItem | HireItem;
  }[];
  potentialDeliveryItems: {
    id: number;
    name: string;
    needsAssigned: boolean;
  }[];
}
